import {
  type Booking,
  type Mode,
  type Passenger,
  type Plan,
  type PlanStop,
  type TicketLeg,
  type TicketRules,
  live,
} from '@trippath/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { iataOf } from '../../lib/airports';
import { daysAfter, lasting, minutesSaid, wallOf } from '../../lib/clock';
import { ModeIcon } from '../icons/ModeIcon';
import { byRoad, kmApart } from '../../lib/map';
import { alongRoad, keyOf, useRoads, wayAsk } from '../../lib/roads';
import { legEnds } from './LegEnds';
import { placeOf } from '../../lib/seats';
import { NO_RULES, type Sector } from '../booking/TicketEditor';
import { useSeatPlans } from '../booking/useSeatPlans';

/** A ticket as it is travelled: its legs in order, each end's zone known where it can be. */
export interface Journey {
  booking: Booking;
  stop: PlanStop;
  /** Its legs, or one from its stop's way on when it says none. */
  legs: [TicketLeg, ...TicketLeg[]];
  /** The number of its first leg among all the trip's legs, from 1. */
  first: number;
}

/** When a journey leaves, to sort by: its first leg's instant, else its day, else its stop's. */
const leaving = (journey: Omit<Journey, 'first'>) =>
  journey.legs[0].departureAt ?? `${journey.booking.day ?? journey.stop.end}T00:00:00Z`;

/** A ticket that says no legs, as the one its stop's way on is. */
function wayLeg(booking: Booking, stop: PlanStop, next: PlanStop | undefined): TicketLeg {
  const out = stop.out;
  return {
    origin: iataOf(out?.from?.name) ?? stop.city,
    destination: iataOf(out?.to?.name) ?? next?.city ?? '',
    departureAt: out?.departureAt ?? null,
    arrivalAt: out?.arrivalAt ?? null,
    actualDepartureAt: null,
    actualArrivalAt: null,
    number: booking.flight?.number ?? null,
    airline: null,
    airlineName: booking.flight?.airline ?? null,
    operator: null,
    vehicle: null,
    travelClass: null,
    carried: null,
    carriedPlate: null,
    registration: null,
    gate: null,
    arrivalGate: null,
    boarding: null,
    deplaning: null,
    terminalFrom: null,
    terminalTo: null,
    km: null,
    originZone: null,
    destinationZone: null,
  };
}

/** How a leg goes: by air when an airline flies it, else as its way does. */
export function legMode(leg: TicketLeg, mode: Mode | undefined): Mode | 'none' {
  return leg.airline === null ? (mode ?? 'none') : 'flight';
}

/** A passenger's place on a leg: their seat, by train with its coach; none when neither. */
export function placeOn(
  passenger: Passenger,
  index: number,
  t: (key: string, options: Record<string, string>) => string,
): string | null {
  const seat = passenger.seats[index] ?? null;
  const coach = passenger.coaches[index] ?? null;
  if (coach === null) return seat;
  return seat === null
    ? t('journeys.coach', { coach })
    : t('journeys.coachSeat', { coach, seat });
}

/** What a leg says of itself besides its vehicle: its class, a vehicle taken aboard. */
export function extrasOf(
  leg: TicketLeg,
  t: (key: string, options: Record<string, string>) => string,
): string[] {
  return [
    leg.travelClass,
    leg.carried === null
      ? null
      : [
          t('journeys.carried', { kind: t(`ticket.carriedKind.${leg.carried}`, {}) }),
          leg.carriedPlate,
        ]
          .filter((part) => part !== null)
          .join(' · '),
  ].filter((part) => part !== null);
}

/**
 * Every ticket of a trip as it is travelled, by when it leaves: each leg's ends in their zones —
 * the airport's, else the stop's it leaves from and the next one's — and numbered on from the last.
 */
export function journeys(plan: Plan): Journey[] {
  const found = plan.stops.flatMap((stop, at) => {
    const next = plan.stops[at + 1];
    return stop.bookings
      .filter((booking) => booking.kind === 'ticket' && live(booking))
      .map((booking) => {
        const zoned = (leg: TicketLeg): TicketLeg => ({
          ...leg,
          originZone: leg.originZone ?? stop.zone,
          destinationZone: leg.destinationZone ?? next?.zone ?? stop.zone,
        });
        const [head = wayLeg(booking, stop, next), ...rest] = booking.legs;
        const legs: Journey['legs'] = [zoned(head), ...rest.map(zoned)];
        return { booking, stop, legs };
      });
  });
  found.sort((a, b) => leaving(a).localeCompare(leaving(b)));
  let count = 1;
  return found.map((journey) => {
    const first = count;
    count += journey.legs.length;
    return { ...journey, first };
  });
}

/** `T 28.10`, or `T–K 28.–29.10` across days: weekdays, then the dates. */
function daySpan(from: string, to: string, lang: string): { weekdays: string; dates: string } {
  const weekday = (day: string) =>
    new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(
      new Date(`${day}T00:00:00Z`),
    );
  const date = (day: string) => `${String(Number(day.slice(8, 10)))}.${day.slice(5, 7)}`;
  if (from === to) return { weekdays: weekday(from), dates: date(from) };
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return {
    weekdays: `${weekday(from)}–${weekday(to)}`,
    dates: sameMonth
      ? `${String(Number(from.slice(8, 10)))}.–${date(to)}`
      : `${date(from)}–${date(to)}`,
  };
}

/** `19:00–23:15`, `23:00–09:10 +1`: two instants local at each end, or what is said of them. */
function clockSpan(
  departs: string | null,
  arrives: string | null,
  from: string | null,
  to: string | null,
): ReactNode {
  const left = departs === null ? '…' : wallOf(departs, from).time;
  const came = arrives === null ? '…' : wallOf(arrives, to).time;
  const days = departs !== null && arrives !== null ? daysAfter(departs, arrives, from, to) : 0;
  // Its dash under the heading's: what leaves before it, what arrives after, a day or more on
  // said small above it, `00:25⁺¹`.
  return (
    <span className="journey-span">
      <span>{left}</span>
      <span>–</span>
      <span>
        {came}
        {days > 0 && (
          <>
            {' '}
            <sup className="day-later">+{days}</sup>
          </>
        )}
      </span>
    </span>
  );
}

/** `27.10 18:00`: an instant local in a zone. */
function moment(at: number, zone: string | null): string {
  const shown = wallOf(new Date(at).toISOString(), zone);
  return `${String(Number(shown.day.slice(8, 10)))}.${shown.day.slice(5, 7)} ${shown.time}`;
}

/** What a ticket allows, said in short lines: its bags, and when check-in is open. */
function useRuleLines() {
  const { t } = useTranslation();
  return (rules: TicketRules, departs: string | null, zone: string | null): string[] => {
    const lines: string[] = [];
    const cabin = [
      rules.cabinPieces === null ? null : t('journeys.pieces', { count: rules.cabinPieces }),
      rules.cabinKg === null ? null : t('journeys.kg', { kg: rules.cabinKg }),
      rules.cabinSize,
    ].filter((part) => part !== null);
    if (cabin.length > 0 || rules.personalItem) {
      lines.push(
        t('journeys.cabin', {
          what: [...cabin, ...(rules.personalItem ? [t('journeys.personal')] : [])].join(' · '),
        }),
      );
    }
    if (rules.checkedBags.length > 0) {
      const [first] = rules.checkedBags;
      const same = rules.checkedBags.every((kg) => kg === first);
      lines.push(
        t('journeys.checked', {
          kg:
            same && rules.checkedBags.length > 1
              ? `${String(rules.checkedBags.length)} × ${String(first)}`
              : rules.checkedBags.join(' + '),
        }),
      );
    }
    const checkIn = (
      where: 'online' | 'desk',
      hours: number | null,
      minutes: number | null,
    ) => {
      if (hours === null && minutes === null) return;
      const at = departs === null ? null : Date.parse(departs);
      const opens =
        hours === null
          ? null
          : at === null
            ? t('journeys.hoursBefore', { count: hours })
            : moment(at - hours * 3_600_000, zone);
      const closes =
        minutes === null
          ? null
          : at === null
            ? t('journeys.minutesBefore', { count: minutes })
            : moment(at - minutes * 60_000, zone);
      const span =
        opens !== null && closes !== null
          ? `${opens} – ${closes}`
          : opens !== null
            ? t('journeys.opens', { at: opens })
            : t('journeys.closes', { at: closes });
      lines.push(t(`journeys.${where}`, { span }));
    };
    checkIn('online', rules.onlineOpensH, rules.onlineClosesM);
    checkIn('desk', rules.deskOpensH, rules.deskClosesM);
    return lines;
  };
}

/** How late a leg left, in minutes — else how late it arrived — when it is known how it went. */
export function delayOf(leg: TicketLeg): number | null {
  const late = (planned: string | null, real: string | null) =>
    planned !== null && real !== null
      ? Math.round((Date.parse(real) - Date.parse(planned)) / 60_000)
      : null;
  return (
    late(leg.departureAt, leg.actualDepartureAt) ?? late(leg.arrivalAt, leg.actualArrivalAt)
  );
}

/** A zone as its offset from UTC at an instant: `UTC+3`. */
function offsetOf(zone: string | null, at: string): string {
  return new Intl.DateTimeFormat('en', { timeZone: zone ?? 'UTC', timeZoneName: 'shortOffset' })
    .formatToParts(new Date(at))
    .map((part) => (part.type === 'timeZoneName' ? part.value : ''))
    .join('')
    .replace('GMT', 'UTC');
}

/** Totals of the trip's journeys: legs, kilometres, time on the way, delays, carriers. */
function Totals({ all, km }: { all: Journey[]; km: number }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const legs = all.flatMap((j) => j.legs);
  const minutes = legs.reduce(
    (sum, leg) =>
      leg.departureAt === null || leg.arrivalAt === null
        ? sum
        : sum + (Date.parse(leg.arrivalAt) - Date.parse(leg.departureAt)) / 60_000,
    0,
  );
  const delays = legs.flatMap((leg) => {
    const late = delayOf(leg);
    return late === null ? [] : [late];
  });
  const late = delays.filter((d) => d >= 15);
  const carriers = [
    ...new Set(legs.flatMap((leg) => (leg.airline === null ? [] : [leg.airline]))),
  ];
  return (
    <dl className="panel journey-totals">
      <div>
        <dt>{t('journeys.totals.legs')}</dt>
        <dd className="mono">{legs.length}</dd>
      </div>
      <div>
        <dt>{t('journeys.totals.km')}</dt>
        <dd className="mono">{km.toLocaleString(lang)}</dd>
      </div>
      <div>
        <dt>{t('journeys.totals.onTheWay')}</dt>
        <dd className="mono">{minutesSaid(Math.round(minutes))}</dd>
      </div>
      <div>
        <dt>{t('journeys.totals.late')}</dt>
        <dd className="mono">
          {late.length} / {delays.length}
        </dd>
        {late.length > 0 && (
          <dd className="quiet journey-totals-sub">
            {t('journeys.totals.lateBy', {
              minutes: Math.round(late.reduce((a, b) => a + b, 0) / late.length),
            })}
          </dd>
        )}
      </div>
      <div>
        <dt>{t('journeys.totals.carriers')}</dt>
        <dd className="mono">{carriers.length}</dd>
        {carriers.length > 0 && (
          <dd className="quiet journey-totals-sub">{carriers.join(' · ')}</dd>
        )}
      </div>
    </dl>
  );
}

/**
 * A leg's times as planned or as they went, as the table is set, at each end's clock or all at
 * home's; gone otherwise than planned, by how much. Each end's zone said when pointed at.
 */
function LegTimes({
  at,
  leg,
  home,
  clock,
  went,
}: {
  /** When it leaves, else arrives: the instant its zones' offsets are said at. */
  at: string;
  leg: TicketLeg;
  home: string;
  clock: 'local' | 'home';
  went: 'plan' | 'real';
}) {
  const { t } = useTranslation();
  const [from, to] = clock === 'local' ? [leg.originZone, leg.destinationZone] : [home, home];
  const real = leg.actualDepartureAt !== null || leg.actualArrivalAt !== null;
  const delay = delayOf(leg);
  const late = delay !== null && delay >= 15;
  const shown =
    went === 'real' && real
      ? clockSpan(leg.actualDepartureAt, leg.actualArrivalAt, from, to)
      : clockSpan(leg.departureAt, leg.arrivalAt, from, to);
  // As it went: the time itself on green, or red when late; by how much when pointed at.
  const zones = t('journeys.zones', {
    from: `${leg.origin} ${offsetOf(leg.originZone, at)}`,
    to: `${leg.destination} ${offsetOf(leg.destinationZone, at)}`,
    home: `${home} ${offsetOf(home, at)}`,
  });
  const how = went === 'real' && delay !== null;
  return (
    <span
      className={
        how
          ? `mono journey-plan ${late ? 'journey-time-late' : 'journey-time-ok'}`
          : 'mono journey-plan'
      }
      title={
        how
          ? `${t('journeys.delay', {
              by: `${delay > 0 ? '+' : delay < 0 ? '−' : '±'}${String(Math.abs(delay))}`,
            })}\n${zones}`
          : zones
      }
    >
      {shown}
    </span>
  );
}

/** The side of the aircraft one sits on: it from above, nose up, that side's wing lit. */
function SideIcon({ side, said }: { side: 'left' | 'right' | 'centre'; said: string }) {
  return (
    <span className="journey-side" role="img" aria-label={said} title={said}>
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        {/* Its wings, the side sat on lit. */}
        {(
          [
            ['left', 'M10.5 9 2 13.5v2l8.5-2.5Z'],
            ['right', 'M13.5 9 22 13.5v2l-8.5-2.5Z'],
          ] as const
        ).map(([wing, d]) => (
          <path
            key={wing}
            className={wing === side ? 'journey-side-on' : 'journey-side-off'}
            d={d}
          />
        ))}
        <path
          className="journey-side-off"
          d="M12 1.5c1 0 1.5 1.5 1.5 3V18l2.5 2v1.5l-4-1-4 1V20l2.5-2V4.5c0-1.5.5-3 1.5-3Z"
        />
      </svg>
    </span>
  );
}

/**
 * The trip's journeys in one table, as a traveller keeps them: each ticket's legs in order —
 * where, when local at each end, as it really went and at home — its flights, planes, seats and
 * gates, and what it allows. On a phone each ticket is a card.
 */
export function Journeys({
  plan,
  home = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onOpen,
}: {
  plan: Plan;
  /** The zone home time is said in. */
  home?: string;
  /** A ticket opened, at one of its legs when that was pressed. */
  onOpen: (booking: Booking, stop: string, sector: Sector | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ruleLines = useRuleLines();
  const all = journeys(plan);
  const planFor = useSeatPlans(all.flatMap((j) => j.legs));
  // On the ground, a ticket of one leg is as far as its way goes along the road: Tartu to
  // Pärnu 170 km; a flight as far as its airports are apart.
  const ground = (stop: PlanStop) => {
    const out = stop.out;
    return out?.from && out.to && byRoad(out.mode) ? wayAsk(out.from, out.to, out.mode) : null;
  };
  const roads = useRoads(all.flatMap((j) => [ground(j.stop)].filter((way) => way !== null)));
  const kmOf = (leg: TicketLeg, journey: Journey): number | null => {
    const way = journey.legs.length === 1 ? ground(journey.stop) : null;
    if (leg.km !== null || way === null) return leg.km;
    const road = alongRoad(journey.stop.out?.mode, roads.get(keyOf(way)));
    return road ? (way.sea ? road.ferryKm : road.km) : Math.round(kmApart(way.from, way.to));
  };
  // Each end's own clock, or every time at home's.
  const [clock, setClock] = useState<'local' | 'home'>('local');
  // The times as planned, or as they went: the switch only where some went otherwise.
  const [went, setWent] = useState<'plan' | 'real'>('plan');
  const anyReal = all.some((j) =>
    j.legs.some((leg) => leg.actualDepartureAt !== null || leg.actualArrivalAt !== null),
  );

  if (all.length === 0) {
    return (
      <section className="panel journeys-none">
        <p className="quiet">{t('journeys.none')}</p>
      </section>
    );
  }

  return (
    <section className="journeys-view" aria-labelledby="journeys-title">
      <h2 id="journeys-title" className="visually-hidden">
        {t('journeys.title')}
      </h2>
      <Totals
        all={all}
        km={all.reduce(
          (sum, j) => sum + j.legs.reduce((n, leg) => n + (kmOf(leg, j) ?? 0), 0),
          0,
        )}
      />
      <div className="panel journeys-panel">
        <div className="journeys-switches">
          {anyReal && (
            <div
              className="segmented segmented-small"
              role="group"
              aria-label={t('journeys.went')}
            >
              {(['plan', 'real'] as const).map((one) => (
                <button
                  key={one}
                  type="button"
                  aria-pressed={went === one}
                  onClick={() => {
                    setWent(one);
                  }}
                >
                  {t(`journeys.${one}`)}
                </button>
              ))}
            </div>
          )}
          <div
            className="segmented segmented-small"
            role="group"
            aria-label={t('journeys.clock')}
          >
            {(['local', 'home'] as const).map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={clock === one}
                onClick={() => {
                  setClock(one);
                }}
              >
                {t(`journeys.${one}`)}
              </button>
            ))}
          </div>
        </div>
        <table className="journeys">
          <thead>
            <tr>
              <th scope="col">{t('journeys.ticket')}</th>
              <th scope="col">{t('journeys.route')}</th>
              <th scope="col">
                <span className="journey-span">
                  <span>{t('journeys.leaves')}</span>
                  <span>–</span>
                  <span>{t('journeys.arrives')}</span>
                </span>
              </th>
              <th scope="col">{t('journeys.flight')}</th>
              <th scope="col">{t('journeys.aircraft')}</th>
              <th scope="col">{t('journeys.seats')}</th>
              <th scope="col">{t('journeys.gates')}</th>
              <th scope="col">{t('journeys.rules')}</th>
            </tr>
          </thead>
          {all.map((journey) => {
            const { booking, stop, legs, first } = journey;
            const head = legs[0];
            const last = legs[legs.length - 1];
            const startDay = head.departureAt
              ? wallOf(head.departureAt, head.originZone).day
              : null;
            const endDay = last?.arrivalAt
              ? wallOf(last.arrivalAt, last.destinationZone).day
              : null;
            const span =
              startDay === null
                ? booking.day === null
                  ? null
                  : daySpan(booking.day, booking.day, lang)
                : daySpan(startDay, endDay ?? startDay, lang);
            // Its legs' numbers among the trip's, said to screen readers and when pointed at.
            const numbers =
              legs.length > 1
                ? t('journeys.numbers', { first, last: first + legs.length - 1 })
                : String(first);
            // Its bags for all, then each person's own where theirs differ.
            const rules = [
              ...ruleLines(booking.rules, head.departureAt, head.originZone),
              ...booking.passengers.flatMap((p) =>
                p.bags === null
                  ? []
                  : [
                      `${p.name}: ${ruleLines(
                        { ...NO_RULES, cabinKg: p.bags.cabinKg, checkedBags: p.bags.checked },
                        null,
                        null,
                      ).join(' · ')}`,
                    ],
              ),
            ];
            // One leg, its name no more than where it goes: said once, by its way.
            const said = booking.name.toLowerCase();
            const plain =
              legs.length === 1 &&
              said.includes(head.origin.toLowerCase()) &&
              said.includes(head.destination.toLowerCase());
            return (
              <tbody key={booking.id} className="journey">
                {legs.map((leg, index) => {
                  const at = leg.departureAt ?? leg.arrivalAt;
                  const seats = booking.passengers
                    .map((p) => p.seats[index] ?? null)
                    .filter((seat) => seat !== null);
                  const places = booking.passengers
                    .map((p) => placeOn(p, index, t))
                    .filter((place) => place !== null);
                  const extras = extrasOf(leg, t);
                  const by = leg.airlineName ?? leg.operator;
                  const gates = legEnds(leg, legMode(leg, stop.out?.mode), t);
                  // The side the seats are on, from the aircraft's seat map, when they share one.
                  const seatPlan = planFor(leg);
                  const sides = new Set(
                    seats.map((seat) => (seatPlan ? placeOf(seatPlan, seat)?.side : undefined)),
                  );
                  const [side] = sides.size === 1 ? [...sides] : [undefined];
                  // As planned, or as it went where that is known.
                  const [goes, comes] =
                    went === 'real' &&
                    leg.actualDepartureAt !== null &&
                    leg.actualArrivalAt !== null
                      ? [leg.actualDepartureAt, leg.actualArrivalAt]
                      : [leg.departureAt, leg.arrivalAt];
                  const took = goes !== null && comes !== null ? lasting(goes, comes) : null;
                  const km = kmOf(leg, journey);
                  return (
                    <tr key={index} className={index === 0 ? 'journey-first' : undefined}>
                      {index === 0 && (
                        <th scope="rowgroup" rowSpan={legs.length} className="journey-ticket">
                          {span && (
                            <span
                              className="journey-days"
                              title={t('journeys.legsOf', { numbers })}
                            >
                              <span>{span.weekdays}</span>{' '}
                              <span className="mono">{span.dates}</span>
                            </span>
                          )}
                          {!plain && (
                            <button
                              type="button"
                              className="link-button journey-name"
                              aria-label={t('journeys.open', { name: booking.name })}
                              onClick={() => {
                                onOpen(booking, stop.id, null);
                              }}
                            >
                              {booking.name}
                            </button>
                          )}
                          {booking.reference !== null && (
                            <span className="mono journey-pnr">
                              {t(
                                stop.out?.mode === 'flight'
                                  ? 'journeys.pnr'
                                  : 'journeys.reference',
                                {
                                  ref: booking.reference,
                                },
                              )}
                            </span>
                          )}
                        </th>
                      )}
                      <td data-label={t('journeys.route')}>
                        <span className="journey-route">
                          <ModeIcon mode={stop.out?.mode} />
                          {/* Its name only its way again: the way itself opens it. */}
                          {plain ? (
                            <button
                              type="button"
                              className="link-button journey-name mono"
                              aria-label={t('journeys.open', { name: booking.name })}
                              onClick={() => {
                                onOpen(booking, stop.id, null);
                              }}
                            >
                              {leg.origin} → {leg.destination}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="link-button journey-name mono"
                              aria-label={t('journeys.openLeg', {
                                leg: `${leg.origin} → ${leg.destination}`,
                              })}
                              onClick={() => {
                                onOpen(booking, stop.id, {
                                  origin: leg.origin,
                                  destination: leg.destination,
                                });
                              }}
                            >
                              {leg.origin} → {leg.destination}
                            </button>
                          )}
                        </span>
                        {(km !== null || took !== null) && (
                          <span className="quiet journey-sub">
                            {[
                              km === null
                                ? null
                                : t('journeys.km', { km: km.toLocaleString(lang) }),
                              took,
                            ]
                              .filter((part) => part !== null)
                              .join(' · ')}
                          </span>
                        )}
                      </td>
                      <td data-label={t('journeys.times')}>
                        {at === null ? (
                          // None said: under the heading's dash.
                          <span className="journey-span quiet">
                            <span />
                            <span>—</span>
                            <span />
                          </span>
                        ) : (
                          <LegTimes at={at} leg={leg} home={home} clock={clock} went={went} />
                        )}
                      </td>
                      <td data-label={t('journeys.flight')} className="mono">
                        {leg.number ?? <span className="quiet">—</span>}
                        {by !== null && (
                          <span className="journey-sub quiet journey-airline">{by}</span>
                        )}
                      </td>
                      <td data-label={t('journeys.aircraft')}>
                        {leg.vehicle === null &&
                        leg.registration === null &&
                        extras.length === 0 ? (
                          <span className="quiet">—</span>
                        ) : (
                          <>
                            {leg.vehicle !== null && (
                              <span className="mono">{leg.vehicle}</span>
                            )}
                            {leg.registration !== null && (
                              <span className="mono quiet journey-sub">{leg.registration}</span>
                            )}
                            {extras.map((extra) => (
                              <span key={extra} className="journey-sub">
                                {extra}
                              </span>
                            ))}
                          </>
                        )}
                      </td>
                      <td data-label={t('journeys.seats')}>
                        {places.length === 0 ? (
                          <span className="quiet">—</span>
                        ) : (
                          <span className="mono journey-seats">
                            {places.join(', ')}
                            {side && <SideIcon side={side} said={t(`seatMap.side.${side}`)} />}
                          </span>
                        )}
                      </td>
                      <td data-label={t('journeys.gates')}>
                        {gates.length === 0 ? (
                          <span className="quiet">—</span>
                        ) : (
                          <span className="journey-gates">{gates}</span>
                        )}
                      </td>
                      {index === 0 && (
                        <td
                          data-label={t('journeys.rules')}
                          rowSpan={legs.length}
                          className="journey-rules"
                        >
                          {rules.length === 0 && booking.rules.notes === '' ? (
                            <span className="quiet">—</span>
                          ) : (
                            <>
                              {rules.map((line) => (
                                <span key={line} className="journey-sub">
                                  {line}
                                </span>
                              ))}
                              {booking.rules.notes !== '' && (
                                <span className="journey-sub quiet">{booking.rules.notes}</span>
                              )}
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      </div>
    </section>
  );
}
